import { Flexbox, Modal, Text } from '@sede-enterprise/shell-ds-react-framework';
import { useEffect, useContext, useReducer, useState } from 'react';
import {
  PIPELINE_GAS,
  POWER,
  AMERICAS,
  ASIA,
  AUSTRALIA,
  FOOT_NOTE,
  ASSET_SCOPE1_CI,
  NATURAL_GAS,
  RNG,
} from '../../api/constants';
import Step3Image from '../../images/Step3_empty.png';
import '../CalculatorForm/CalculatorForm.css';
import { AppContext } from '../../context/calculatorFormContext';
import { kpiContentReducer } from '../../reducers/ActionHandlers/KpiContentActionHandler';
import { kpiContentInitialState } from '../../reducers/InitialStates/KpiContentInitialState';
import { KpiCardView } from './KpiCardView';
import { KpiMessageView } from './KpiMessageView';
import { KpiTable } from './KpiTable';
import {
  decorateTableData,
  decorateCardTableData,
  createDealsFromInput,
  getNcidataForStepThreeTable,
} from './KpiContentFunctions';
import { calculateImpactYearNCIDeals } from '../AllDeals/DealDetails/DealDetailFunctions';
import { useNetCarbonIntensity } from '../../context/NetCarbonIntensityContext';
import type { IKpiContentData } from '../../types/types';
import type { Dispatch } from 'react';
import { FIRST_COL_DATA, FIRST_COL_DATA_SLMT_TS, one } from '../../constants';
import { getCols } from './utils';

const KPIContent = (props?: { kpiState?: IKpiContentData; kpiDispatch?: Dispatch<any> }) => {
  const { state, dispatch } = useContext(AppContext);
  const [kpiMessage, setKpiMessage] = useState<string[]>([]);
  // Allow CalculatorForm to pass down the KPI reducer so saving uses the same state
  const [localKpiState, localKpiDispatch] = useReducer(kpiContentReducer, kpiContentInitialState);
  const myState = props?.kpiState ?? localKpiState;
  const myDispatch = props?.kpiDispatch ?? localKpiDispatch;
  const { netCarbonIntensityData: nciData } = useNetCarbonIntensity();
  // combination of Business and Commodity
  const isAmericasWithCommodityPg = state.commodity === PIPELINE_GAS && state.region === AMERICAS;
  const isAmericasWithCommodityRng = state.commodity === RNG && state.region === AMERICAS;
  const isAsiaWithCommodityPg = state.commodity === PIPELINE_GAS && state.region === ASIA;
  const isAsiaWithCommodityRng = state.commodity === RNG && state.region === ASIA;
  const isAustraliaWithCommodityPg = state.commodity === PIPELINE_GAS && state.region === AUSTRALIA;
  const isAustraliaWithCommodityRng = state.commodity === RNG && state.region === AUSTRALIA;
  const isCommodityPW = state.commodity === POWER;
  const isCommodityPg = state.commodity === PIPELINE_GAS;
  const isCommodityRng = state.commodity === RNG;

  const isGasFiredPower =
    state.technologySource.includes(NATURAL_GAS) || state.technologySource.includes('LNG');

  // Helper function to normalize EVP name for comparison
  const normalizeEvpName = (evpName: string): string => {
    return evpName.toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  // Check if EVP is T&S Products
  // T&S Products EVP requires additional KPIs: Volume-weighted Avg CI and Scope 3 Cat11 Emission
  // SLMT uses standard KPI calculations like other EVPs
  const normalizedEvp = normalizeEvpName(state.evp);
  const isTSProducts = normalizedEvp === 'tsproducts';
  const isSLMT = normalizedEvp === 'slmt';
  const shouldShowFootnote = !isTSProducts && !isSLMT;

  // Use extended KPI list for T&S Products only, standard list for other EVPs (including SLMT)
  let firstColData = [...(isTSProducts ? FIRST_COL_DATA_SLMT_TS : FIRST_COL_DATA)];

  // Adding new field at beginning of the firstColData array, if the selected technology source is 'Gas Fired Generation'
  if (isGasFiredPower) {
    firstColData = [ASSET_SCOPE1_CI, ...firstColData];
  }
  const parameters = {
    isAmericasWithCommodityPg,
    isAmericasWithCommodityRng,
    isAsiaWithCommodityPg,
    isAsiaWithCommodityRng,
    isAustraliaWithCommodityPg,
    isAustraliaWithCommodityRng,
    isCommodityPW,
    isCommodityPg,
    isCommodityRng,
    isGasFiredPower,
  };
  // set all the column headers
  const cols = getCols(state, state.stepTwoTableData);

  useEffect(() => {
    decorateTableData(state, dispatch, myState, myDispatch, setKpiMessage, {
      parameters,
      cols,
      firstColData,
    });
  }, [state.isGetOutputKpi]);

  useEffect(() => {
    decorateCardTableData(myState, myDispatch, dispatch, parameters, state);
  }, [myState.stepThreeTableData]);

  useEffect(() => {
    decorateCardTableData(myState, myDispatch, dispatch, parameters, state);
  }, [state.isGetOutputKpi]);

  const onClose = () => {
    myDispatch({
      type: 'setIsCopiedToClipboard',
      payload: false,
    });
  };
  const netAbsEmissionYearWise = myState.netAbsEmsDataYearWise;
  const { stepTwoTableData, commodity, uomVal, operatingPlan } = state;
  const volumeYearWise = stepTwoTableData[stepTwoTableData.length - one];
  const getDeal = createDealsFromInput(netAbsEmissionYearWise, commodity, volumeYearWise, uomVal);
  const impactNciData = getDeal && calculateImpactYearNCIDeals(getDeal, nciData, operatingPlan);
  // Keep impactNciData in KPI state up to date for save logic
  useEffect(() => {
    if (
      impactNciData &&
      // Only dispatch if data is actually different (shallow compare by length and years)
      (!myState.impactNciData ||
        impactNciData.length !== myState.impactNciData.length ||
        impactNciData.some((item, idx) =>
          !myState.impactNciData?.[idx] || item.YEAR !== myState.impactNciData[idx].YEAR || item.CHANGED_REPORTED_GROUP_NCI !== myState.impactNciData[idx].CHANGED_REPORTED_GROUP_NCI
        )
      )
    ) {
      myDispatch({ type: 'setImpactNciData', payload: impactNciData });
    }
  }, [impactNciData, myState.impactNciData]);
  const stepThreeNciData =
    impactNciData &&
    getNcidataForStepThreeTable(getDeal, impactNciData, myState.stepThreeTableData, state.evp, state.stepTwoTableData, state);

  // Extract year-wise NCI Impact from stepThreeNciData and store in state
  useEffect(() => {
    if (stepThreeNciData) {
      const nciImpactRow = stepThreeNciData.find((row: any) => row.KPI === 'NCI Impact (gCO2e/MJ)');
      if (nciImpactRow) {
        // Remove non-year keys (KPI, Total, etc.)
        const yearWise: Record<number, string> = {};
        Object.keys(nciImpactRow).forEach((key) => {
          if (/^\d{4}$/.test(key)) {
            yearWise[Number(key)] = nciImpactRow[key as keyof typeof nciImpactRow] as string;
          }
        });
        // Only dispatch if yearWise is actually different from myState.nciImpactYearWise
        const prev = myState.nciImpactYearWise || {};
        const keys = Object.keys(yearWise);
        const prevKeys = Object.keys(prev);
        const isDifferent = keys.length !== prevKeys.length || keys.some(k => yearWise[Number(k)] !== prev[Number(k)]);
        if (isDifferent) {
          myDispatch({ type: 'setNciImpactYearWise', payload: yearWise });
        }
      }
    }
  }, [stepThreeNciData]);

  // FIX: For T&S Products and SLMT, use the updated nciData (which includes our NCI Impact calculations)
  // instead of impactNciData for the KPI Summary card
  const isTSProductsOrSLMT = normalizedEvp === 'tsproducts' || normalizedEvp === 'slmt';
  
  let nciDataForSummaryCard = nciData;
  
  // Special handling for T&S Products/SLMT
  if (isTSProductsOrSLMT && impactNciData) {
    nciDataForSummaryCard = nciData.map(nciItem => {
      const impactItem = impactNciData.find(impact => impact.YEAR === nciItem.YEAR);
      
      // If nciData doesn't have CHANGED_REPORTED_GROUP_NCI but impactNciData does, use impactNciData
      if (!nciItem.hasOwnProperty('CHANGED_REPORTED_GROUP_NCI') && 
          impactItem && impactItem.hasOwnProperty('CHANGED_REPORTED_GROUP_NCI')) {
        return impactItem;
      }
      
      return nciItem;
    });
  }

  // Unified handling for all other EVPs (DR Power, Shell Energy, etc.)
  if (!isTSProductsOrSLMT && impactNciData) {
    nciDataForSummaryCard = nciData.map(nciItem => {
      const impactItem = impactNciData.find(impact => impact.YEAR === nciItem.YEAR);
      
      // If impactNciData has CHANGED_REPORTED_GROUP_NCI but nciData doesn't, use impactNciData
      if (impactItem && impactItem.hasOwnProperty('CHANGED_REPORTED_GROUP_NCI') && 
          !nciItem.hasOwnProperty('CHANGED_REPORTED_GROUP_NCI')) {
        return impactItem;
      }
      
      return nciItem;
    });
  }

  return !state.isGetOutputKpiServiceFailure && state.isShowKpiData && state.isFormValidated ? (
    <>
      <Modal
        open={myState.isCopiedToClipboard}
        onClose={onClose}
        showFooter={false}
        showHeader={false}
      >
        {(myState.copiedContent as string) + ' is copied to clipboard.'}
      </Modal>
      <Flexbox gap='20px'>
        <KpiCardView cardTableData={myState.cardTableData} impactNciData={nciDataForSummaryCard} isTSProducts={isTSProducts} isSLMT={isSLMT} />
        <KpiMessageView kpiMessage={kpiMessage} isGasFiredPower={isGasFiredPower} />
      </Flexbox>
      <KpiTable cols={cols} stepThreeNciData={stepThreeNciData || []} isTSProducts={isTSProducts} />
      {/* Hide footnote for T&S Products and SLMT EVPs */}
      {shouldShowFootnote && (
        <Text size='small' style={{ color: '#00000' }}>
          <em>{FOOT_NOTE}</em>
        </Text>
      )}
    </>
  ) : (
    <>
      <Text type='span' className='kpi-label'></Text>
      <div className='empty-state-image'>
        <img src={Step3Image} width='90' height='90' alt='KPI Table' />
      </div>
      <p className='empty-state-sub-label'>Please complete Step 02 to generate Carbon KPIs</p>
    </>
  );
};
export default KPIContent;
